// Rigid per-cloud transforms and point-to-plane ICP on the "align" volumes.
// Port of ipad-docs/scripts/align_volumes.py (same method and safeguards):
// Huber weight 1 cm, correspondence cut-off geomspace 0.25 -> 0.04 m, yaw only
// with >= 100 points in >= 2 boxes, x/y held when the matched surfaces hardly
// face that way (mean squared normal component <= 0.03), z held unless freed,
// a step rejected if the rms gets worse.
import type { V3 } from './math.ts';

/** p' = R p + t, R row-major 3x3. */
export interface Rigid { R: number[]; t: V3 }

export const identity = (): Rigid => ({ R: [1, 0, 0, 0, 1, 0, 0, 0, 1], t: [0, 0, 0] });

export const isIdentity = (a: Rigid, eps = 1e-9) =>
  a.R.every((x, i) => Math.abs(x - (i % 4 === 0 ? 1 : 0)) < eps) && a.t.every(x => Math.abs(x) < eps);

/** a after b. */
export function compose(a: Rigid, b: Rigid): Rigid {
  const R: number[] = [];
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) R.push(a.R[3 * i] * b.R[j] + a.R[3 * i + 1] * b.R[3 + j] + a.R[3 * i + 2] * b.R[6 + j]);
  return { R, t: add3(rot(a, b.t), a.t) };
}

export const rot = (a: Rigid, v: V3): V3 => [
  a.R[0] * v[0] + a.R[1] * v[1] + a.R[2] * v[2],
  a.R[3] * v[0] + a.R[4] * v[1] + a.R[5] * v[2],
  a.R[6] * v[0] + a.R[7] * v[1] + a.R[8] * v[2],
];
export const apply = (a: Rigid, p: V3): V3 => add3(rot(a, p), a.t);
const add3 = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];

/** Turn by yaw (radians) about the vertical through c, then shift by d. */
export function yawAbout(yaw: number, c: V3, d: V3 = [0, 0, 0]): Rigid {
  const cs = Math.cos(yaw), sn = Math.sin(yaw);
  const R = [cs, -sn, 0, sn, cs, 0, 0, 0, 1];
  const rc = rot({ R, t: [0, 0, 0] }, c);
  return { R, t: [c[0] - rc[0] + d[0], c[1] - rc[1] + d[1], d[2]] };
}

/** a⁻¹. */
export function inverse(a: Rigid): Rigid {
  const R = [a.R[0], a.R[3], a.R[6], a.R[1], a.R[4], a.R[7], a.R[2], a.R[5], a.R[8]];
  const t = a.t;
  return { R, t: [-(R[0] * t[0] + R[1] * t[1] + R[2] * t[2]), -(R[3] * t[0] + R[4] * t[1] + R[5] * t[2]), -(R[6] * t[0] + R[7] * t[1] + R[8] * t[2])] };
}

export const yawDeg = (a: Rigid) => (Math.atan2(a.R[3], a.R[0]) * 180) / Math.PI;

/** Column-major 4x4 for WebGL. */
export function mat4(a: Rigid): Float32Array {
  const R = a.R;
  return new Float32Array([R[0], R[3], R[6], 0, R[1], R[4], R[7], 0, R[2], R[5], R[8], 0, a.t[0], a.t[1], a.t[2], 1]);
}

/** Row-major 4x4 (for the export / python). */
export const rows4 = (a: Rigid) => [[...a.R.slice(0, 3), a.t[0]], [...a.R.slice(3, 6), a.t[1]], [...a.R.slice(6), a.t[2]], [0, 0, 0, 1]];

export function parseRigid(o: unknown): Rigid | null {
  const r = o as Rigid;
  if (!r || !Array.isArray(r.R) || r.R.length !== 9 || !Array.isArray(r.t) || r.t.length !== 3) return null;
  if (![...r.R, ...r.t].every(x => typeof x === 'number' && Number.isFinite(x))) return null;
  return { R: [...r.R], t: [r.t[0], r.t[1], r.t[2]] };
}

/** Apply a rigid transform to packed xyz (and optional normals) into new arrays. */
export function transformPoints(a: Rigid, xyz: Float32Array, out = new Float32Array(xyz.length), withT = true) {
  const R = a.R, t = withT ? a.t : [0, 0, 0];
  for (let i = 0; i < xyz.length; i += 3) {
    const x = xyz[i], y = xyz[i + 1], z = xyz[i + 2];
    out[i] = R[0] * x + R[1] * y + R[2] * z + t[0];
    out[i + 1] = R[3] * x + R[4] * y + R[5] * z + t[1];
    out[i + 2] = R[6] * x + R[7] * y + R[8] * z + t[2];
  }
  return out;
}

// ── nearest neighbours: a uniform grid over packed xyz ────────────────────
/** Nearest-neighbour search over packed xyz (Grid here; the object page's wasm grid too). */
export interface NN {
  readonly xyz: Float32Array;
  /** Index of the nearest point within maxd of (x, y, z), or -1. */
  nearest(x: number, y: number, z: number, maxd: number): number;
  /** nearest() for each point of q (packed xyz) into out. */
  nearestMany(q: Float32Array, maxd: number, out: Int32Array): void;
}

export class Grid implements NN {
  private cells = new Map<number, [number, number]>();
  private order: Int32Array;
  xyz: Float32Array;
  cell: number;
  constructor(xyz: Float32Array, cell = 0.125) {
    this.xyz = xyz;
    this.cell = cell;
    const n = xyz.length / 3, keys = new Float64Array(n);
    for (let i = 0; i < n; i++) keys[i] = this.key(Math.floor(xyz[3 * i] / cell), Math.floor(xyz[3 * i + 1] / cell), Math.floor(xyz[3 * i + 2] / cell));
    this.order = new Int32Array(n);
    for (let i = 0; i < n; i++) this.order[i] = i;
    this.order.sort((a, b) => keys[a] - keys[b]);
    for (let s = 0; s < n;) {
      let e = s + 1;
      const k = keys[this.order[s]];
      while (e < n && keys[this.order[e]] === k) e++;
      this.cells.set(k, [s, e]);
      s = e;
    }
  }
  private key(x: number, y: number, z: number) { return ((x + 8192) * 16384 + (y + 8192)) * 16384 + (z + 8192); }

  /**
   * Index of the nearest point within maxd of (x, y, z), or -1. Searches shells of cells outwards and
   * stops once nothing further out can be nearer (a point in shell d is at least (d - 1) cells away).
   * ok: only points it accepts count.
   */
  nearest(x: number, y: number, z: number, maxd: number, ok?: (i: number) => boolean): number {
    const c = this.cell, r = Math.ceil(maxd / c), cx = Math.floor(x / c), cy = Math.floor(y / c), cz = Math.floor(z / c);
    let best = -1, bd = maxd * maxd;
    const P = this.xyz, O = this.order;
    const scan = (i: number, j: number, k: number) => {
      const se = this.cells.get(this.key(cx + i, cy + j, cz + k));
      if (!se) return;
      for (let s = se[0]; s < se[1]; s++) {
        const q = 3 * O[s], dx = P[q] - x, dy = P[q + 1] - y, dz = P[q + 2] - z, d = dx * dx + dy * dy + dz * dz;
        if (d <= bd && (!ok || ok(O[s]))) { bd = d; best = O[s]; }
      }
    };
    for (let d = 0; d <= r; d++) {
      if (best >= 0 && bd <= ((d - 1) * c) ** 2) break;   // shell d and beyond are all further than what we have
      for (let i = -d; i <= d; i++) for (let j = -d; j <= d; j++) {
        if (Math.abs(i) === d || Math.abs(j) === d) for (let k = -d; k <= d; k++) scan(i, j, k);
        else { scan(i, j, -d); if (d) scan(i, j, d); }
      }
    }
    return best;
  }

  nearestMany(q: Float32Array, maxd: number, out: Int32Array) {
    for (let i = 0, n = q.length / 3; i < n; i++) out[i] = this.nearest(q[3 * i], q[3 * i + 1], q[3 * i + 2], maxd);
  }

  /** Call f(i) for every point within r of (x, y, z). */
  within(x: number, y: number, z: number, r: number, f: (i: number) => void) {
    const c = this.cell, k = Math.ceil(r / c), cx = Math.floor(x / c), cy = Math.floor(y / c), cz = Math.floor(z / c), r2 = r * r;
    const P = this.xyz, O = this.order;
    for (let i = -k; i <= k; i++) for (let j = -k; j <= k; j++) for (let l = -k; l <= k; l++) {
      const se = this.cells.get(this.key(cx + i, cy + j, cz + l));
      if (!se) continue;
      for (let s = se[0]; s < se[1]; s++) {
        const q = 3 * O[s], dx = P[q] - x, dy = P[q + 1] - y, dz = P[q + 2] - z;
        if (dx * dx + dy * dy + dz * dz <= r2) f(O[s]);
      }
    }
  }
}

// ── ICP ───────────────────────────────────────────────────────────────────
export interface FitResult {
  step: Rigid;          // correction to apply on top (world)
  rms0: number; rms1: number; matched: number;
  free: string[];       // motions that were allowed
  accepted: boolean;
}

export const geomspace = (a: number, b: number, n: number) =>
  Array.from({ length: n }, (_, i) => (n === 1 ? a : a * Math.pow(b / a, i / (n - 1))));

/** Solve the small symmetric system H x = g (Gaussian elimination, n <= 6). */
export function solve(H: number[][], g: number[]): number[] {
  const n = g.length, A = H.map((r, i) => [...r, g[i]]);
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
    [A[c], A[p]] = [A[p], A[c]];
    const d = A[c][c] || 1e-12;
    for (let r = 0; r < n; r++) if (r !== c) { const f = A[r][c] / d; for (let k = c; k <= n; k++) A[r][k] -= f * A[c][k]; }
  }
  return A.map((r, i) => r[n] / (A[i][i] || 1e-12));
}

/**
 * Point-to-plane ICP moving src (packed xyz, world) onto the grid's points with
 * normals qn. Moves allowed: yaw (if canTurn), x, y, and z only if freeZ; each
 * axis also needs the matched surfaces to face it. Rotation is about src's centroid.
 */
export function fit(src: Float32Array, grid: NN, qn: Float32Array, o: FitOpts): FitResult {
  const g = fitSteps(src, grid, qn, o);
  for (;;) { const r = g.next(); if (r.done) return r.value; }
}

export interface FitOpts { iters: number; canTurn: boolean; freeZ: boolean }

/** fit() as a generator that yields after every iteration, so a caller can spread it over frames. */
export function* fitSteps(src: Float32Array, grid: NN, qn: Float32Array, o: FitOpts): Generator<void, FitResult> {
  const n = src.length / 3, Q = grid.xyz;
  let c: V3 = [0, 0, 0];
  for (let i = 0; i < n; i++) for (let k = 0; k < 3; k++) c[k] += src[3 * i + k] / n;
  let cur = identity();
  const p = new Float32Array(src.length);
  /** residuals at a cut-off: matched points p, their target index */
  const match = (maxd: number) => {
    transformPoints(cur, src, p);
    const idx = new Int32Array(n);
    grid.nearestMany(p, maxd, idx);
    return idx;
  };
  const rmsOf = (idx: Int32Array) => {
    let s = 0, m = 0;
    for (let i = 0; i < n; i++) if (idx[i] >= 0) {
      const j = 3 * idx[i], r = (p[3 * i] - Q[j]) * qn[j] + (p[3 * i + 1] - Q[j + 1]) * qn[j + 1] + (p[3 * i + 2] - Q[j + 2]) * qn[j + 2];
      s += r * r; m++;
    }
    return { rms: m ? Math.sqrt(s / m) : NaN, m };
  };
  let idx = match(0.1);
  const r0 = rmsOf(idx);
  // which motions the matched surfaces can pin down, decided once at the start
  const cover = [0, 0, 0];
  for (let i = 0; i < n; i++) if (idx[i] >= 0) for (let k = 0; k < 3; k++) cover[k] += qn[3 * idx[i] + k] ** 2 / r0.m;
  const active = [o.canTurn, cover[0] > 0.03, cover[1] > 0.03, o.freeZ && cover[2] > 0.03];
  const names = ['yaw', 'x', 'y', 'z'].filter((_, i) => active[i]);
  const cols = active.map((a, i) => (a ? i : -1)).filter(i => i >= 0);
  if (cols.length && r0.m) for (const maxd of geomspace(0.25, 0.04, o.iters)) {
    idx = match(maxd);
    const H = cols.map(() => cols.map(() => 0)), g = cols.map(() => 0);
    let m = 0;
    for (let i = 0; i < n; i++) {
      if (idx[i] < 0) continue;
      m++;
      const j = 3 * idx[i], nx = qn[j], ny = qn[j + 1], nz = qn[j + 2];
      const px = p[3 * i], py = p[3 * i + 1], pz = p[3 * i + 2];
      const r = (px - Q[j]) * nx + (py - Q[j + 1]) * ny + (pz - Q[j + 2]) * nz;
      const w = 1 / Math.max(1, Math.abs(r) / 0.01);   // Huber, 1 cm
      const J = [(px - c[0]) * ny - (py - c[1]) * nx, nx, ny, nz];   // d r / d(yaw about c, x, y, z)
      for (let a = 0; a < cols.length; a++) {
        const ja = J[cols[a]];
        g[a] -= w * ja * r;
        for (let b = 0; b < cols.length; b++) H[a][b] += w * ja * J[cols[b]];
      }
    }
    if (m < 100) break;
    for (let a = 0; a < cols.length; a++) H[a][a] += 1e-6;
    const x = solve(H, g), all = [0, 0, 0, 0];
    cols.forEach((col, a) => (all[col] = x[a]));
    cur = compose(yawAbout(all[0], c, [all[1], all[2], all[3]]), cur);   // like the script: turn about the original centroid
    yield;
  }
  idx = match(0.1);
  const r1 = rmsOf(idx);
  const accepted = Number.isFinite(r1.rms) && !(Number.isFinite(r0.rms) && r1.rms > r0.rms);
  return { step: accepted ? cur : identity(), rms0: r0.rms, rms1: accepted ? r1.rms : r0.rms, matched: r1.m, free: names, accepted };
}
